/**
 * hash 路由的纯逻辑与订阅（T01）。
 *
 * 骨架刻意不引入第三方依赖：只把 `location.hash` 归一化成路由路径，
 * 用 `useSyncExternalStore` 同时订阅 `hashchange` 与「写 hash 时的主动通知」，
 * 因此浏览器前进 / 后退与页面内点击切换都能立刻重渲染。
 */
import { useSyncExternalStore } from 'react';

import type { PageDirection } from '../design/motion';
import { DEFAULT_ROUTE, isRoutePath, ROUTES } from './routes';
import type { RoutePath } from './routes';

/**
 * 不占底部导航的「独立页」路径（与 `router.tsx` 的 `STANDALONE_ROUTES` 一一对应）。
 *
 * 这些页面只在 `router.tsx` 的路由层判定，不进 `routes.ts` 的主路由表；但
 * `navigate()` 同样接受它们，方便从设置页 / 角色卡页 / 对话页内联跳转。
 */
export type StandalonePath =
  | '/chat'
  | '/attachments'
  | '/memory'
  | '/providers'
  | '/community'
  | '/gallery'
  | '/account'
  | '/marketplace'
  | '/billing'
  | '/tts'
  | '/admin';

/** `navigate()` accepts main tabs and independent feature pages. */
export type NavigablePath = RoutePath | StandalonePath;

const listeners = new Set<() => void>();

export const STANDALONE_TITLES: Record<StandalonePath, string> = {
  '/chat': '对话',
  '/attachments': '附件',
  '/memory': '记忆管理',
  '/providers': '模型提供商',
  '/community': '栀歆角色卡',
  '/gallery': '图库',
  '/account': '账户',
  '/marketplace': '模型广场',
  '/billing': '充值中心',
  '/tts': '语音与 TTS',
  '/admin': '管理后台',
};

export function isStandalonePath(path: string): path is StandalonePath {
  return Object.prototype.hasOwnProperty.call(STANDALONE_TITLES, path);
}

function normalizeHashPath(hash: string): string {
  const withoutHash = hash.startsWith('#') ? hash.slice(1) : hash;
  const pathOnly = withoutHash.split('?')[0] ?? '';
  const withSlash = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
  const normalized = withSlash.length > 1 && withSlash.endsWith('/') ? withSlash.slice(0, -1) : withSlash;
  if (normalized === '/conversations') return '/messages';
  if (normalized === '/presets') return '/contacts';
  return normalized;
}

/** Normalize the route only; keep valid direct URLs and their queries intact. */
export function parseNavigationPath(hash: string): NavigablePath {
  const trimmed = normalizeHashPath(hash);
  return isRoutePath(trimmed) || isStandalonePath(trimmed) ? trimmed : DEFAULT_ROUTE;
}

/** Compatibility for callers that only understand the four main routes. */
export function parseHashPath(hash: string): RoutePath {
  const path = parseNavigationPath(hash);
  return isRoutePath(path) ? path : DEFAULT_ROUTE;
}

/** 读当前地址栏的路由路径。 */
export function readHashPath(): RoutePath {
  if (typeof window === 'undefined') return DEFAULT_ROUTE;
  return parseHashPath(window.location.hash);
}

interface HistoryEntry {
  session: string;
  index: number;
  path: NavigablePath;
  parent: NavigablePath | null;
}

export interface NavigationSnapshot {
  path: NavigablePath;
  direction: PageDirection;
  canGoBack: boolean;
}

const HISTORY_KEY = '__zhixinNavigation';
const serverSnapshot: NavigationSnapshot = {
  path: DEFAULT_ROUTE,
  direction: 'forward',
  canGoBack: false,
};
let currentEntry: HistoryEntry | undefined;
let snapshot = serverSnapshot;

function historyEntry(): HistoryEntry | undefined {
  const state: unknown = window.history.state;
  if (typeof state !== 'object' || state === null || !(HISTORY_KEY in state)) return undefined;
  const value: unknown = state[HISTORY_KEY];
  if (typeof value !== 'object' || value === null) return undefined;
  const entry = value as Partial<HistoryEntry>;
  return typeof entry.session === 'string' &&
    Number.isInteger(entry.index) &&
    typeof entry.path === 'string' &&
    (isRoutePath(entry.path) || isStandalonePath(entry.path)) &&
    (entry.parent === null ||
      (typeof entry.parent === 'string' &&
        (isRoutePath(entry.parent) || isStandalonePath(entry.parent))))
    ? (entry as HistoryEntry)
    : undefined;
}

function stateWithEntry(entry: HistoryEntry): Record<string, unknown> {
  const state: unknown = window.history.state;
  return {
    ...(typeof state === 'object' && state !== null && !Array.isArray(state)
      ? state
      : { __zhixinPreviousState: state }),
    [HISTORY_KEY]: entry,
  };
}

/** Track history entries; only main-to-main animation follows the tab order. */
export function readNavigation(): NavigationSnapshot {
  if (typeof window === 'undefined') return serverSnapshot;
  const path = parseNavigationPath(window.location.hash);
  let entry = historyEntry();
  if (entry?.path !== path) {
    entry = {
      session: `${Date.now()}-${Math.random()}`,
      index: 0,
      path,
      parent: null,
    };
    window.history.replaceState(stateWithEntry(entry), '');
  }
  const rawPath = normalizeHashPath(window.location.hash);
  if (!isRoutePath(rawPath) && !isStandalonePath(rawPath)) {
    window.history.replaceState(window.history.state, '', `#${path}`);
  }
  if (
    currentEntry?.session === entry.session &&
    currentEntry.index === entry.index &&
    snapshot.path === path
  )
    return snapshot;
  let direction: PageDirection = 'forward';
  if (
    currentEntry &&
    currentEntry.path !== path &&
    isRoutePath(currentEntry.path) &&
    isRoutePath(path)
  ) {
    const previousPath = currentEntry.path;
    const fromIndex = ROUTES.findIndex((route) => route.path === previousPath);
    const toIndex = ROUTES.findIndex((route) => route.path === path);
    direction = toIndex < fromIndex ? 'tab-left' : 'tab-right';
  } else if (currentEntry?.session === entry.session && entry.index < currentEntry.index) {
    direction = 'backward';
  }
  currentEntry = entry;
  snapshot = { path, direction, canGoBack: entry.parent !== null };
  return snapshot;
}

function notify(event?: Event): void {
  // Legacy feature links assign location.hash directly rather than calling navigate().
  if (event && currentEntry && !historyEntry()) {
    const path = parseNavigationPath(window.location.hash);
    const entry: HistoryEntry = {
      session: currentEntry.session,
      index: currentEntry.index + 1,
      path,
      parent: currentEntry.path,
    };
    window.history.replaceState(stateWithEntry(entry), '');
  }
  readNavigation();
  for (const listener of [...listeners]) listener();
}

/**
 * 切换路由：写 `location.hash`（浏览器前进 / 后退可用）并立即通知订阅者。
 *
 * 参数放宽为 `RoutePath | StandalonePath`：主路由行为不变，独立页也可由此进入；
 * 因此「设置页 → 独立页 → 返回主路由」共用同一套 hash 前进 / 后退语义。
 */
export function navigate(path: NavigablePath): void {
  if (typeof window === 'undefined') return;
  readNavigation();
  if (snapshot.path === path) return;
  const previous = currentEntry!;
  const entry: HistoryEntry = {
    session: previous.session,
    index: previous.index + 1,
    path,
    parent: previous.path,
  };
  window.history.pushState(stateWithEntry(entry), '', `#${path}`);
  notify();
}

/** A direct feature link returns inside the app instead of leaving the site. */
export function returnNavigation(): void {
  if (typeof window === 'undefined') return;
  readNavigation();
  if (snapshot.canGoBack) {
    window.history.back();
    return;
  }
  const entry: HistoryEntry = {
    session: currentEntry!.session,
    index: currentEntry!.index - 1,
    path: DEFAULT_ROUTE,
    parent: null,
  };
  window.history.replaceState(stateWithEntry(entry), '', `#${DEFAULT_ROUTE}`);
  notify();
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0 && typeof window !== 'undefined') {
    window.addEventListener('popstate', notify);
    window.addEventListener('hashchange', notify);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('popstate', notify);
      window.removeEventListener('hashchange', notify);
    }
  };
}

export function useNavigation(): NavigationSnapshot {
  return useSyncExternalStore(subscribe, readNavigation, () => serverSnapshot);
}

/** 当前路由（订阅 hash 变化）。 */
export function useHashRoute(): RoutePath {
  return useSyncExternalStore(subscribe, readHashPath, () => DEFAULT_ROUTE);
}
